/* eslint-disable react/prop-types */
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/outline';
import styled from 'styled-components';
import { SettingOutlined, CheckOutlined } from '@ant-design/icons';
import React from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { Markdown } from '@zedoc/react-markdown';
import Text from '../../../common/components/base/Text';
import Box from '../../../common/components/primitives/Box';
import Cover from '../../../common/components/primitives/Cover';
import Stack from '../../../common/components/primitives/Stack';
import Switcher from '../../../common/components/primitives/Switcher';
import Cluster from '../../../common/components/primitives/Cluster';
import Card from '../../../components/Card';
import Button from '../../../components/Button';
import Explanation from './Explanation';

const StyledSwitcher = styled(Switcher)`
  /* Experimental full height */
  &,
  > div,
  > div > div {
    height: 100%;
  }
`;

const Container = ({ isNewPresentation, children }) => {
  if (isNewPresentation) {
    return (
      <div className="pt-6 h-full stack-6 overflow-hidden">{children}</div>
    );
  }

  return <StyledSwitcher space={0}>{children}</StyledSwitcher>;
};

const Layout = ({
  title,
  descriptionsKeys,
  explanations,
  forms,
  preview,

  // FIXME: If we remove Save button then we need to remove all onSave mentions
  // onSave,
  hasPrevTab,
  hasNextTab,
  onOpenPrevTab,
  onOpenNextTab,
  onComplete,
  isNewPresentation,
}) => {
  const { t } = useTranslation();
  return (
    <Container isNewPresentation={isNewPresentation}>
      {forms.map(
        (form) =>
          form && (
            <div
              key={form.key}
              style={{
                overflowY: 'auto',
              }}
              className={isNewPresentation ? 'flex-1 flex px-6' : ''}
            >
              {title && (
                <Box
                  style={{
                    paddingBottom: 0,
                  }}
                >
                  <Text.Heading data-testid={`pw-subheader-${title}`} level={2}>
                    {title}
                  </Text.Heading>
                </Box>
              )}
              {isNewPresentation ? (
                form
              ) : (
                <Box
                  className={`project-wizard-layout-form ${
                    isNewPresentation ? 'flex' : ''
                  }`}
                >
                  {form}
                </Box>
              )}
            </div>
          ),
      )}
      {isNewPresentation ? (
        <div className="cluster-4 justify-end px-6">
          <Button
            data-testid="button-Back"
            icon={<ChevronLeftIcon />}
            onClick={onOpenPrevTab}
            disabled={!hasPrevTab}
          >
            Back
          </Button>
          {hasNextTab ? (
            <Button
              data-testid="button-Next"
              type="primary"
              onClick={onOpenNextTab}
            >
              <div className="cluster-2 items-center">
                <span>Next</span>
                <ChevronRightIcon className="h-5 w-5" />
              </div>
            </Button>
          ) : (
            <Button
              data-testid="button-Complete"
              type="primary"
              icon={<CheckOutlined />}
              onClick={onComplete}
            >
              {t('complete')}
            </Button>
          )}
        </div>
      ) : (
        <Cover
          centeredSelector=".project-wizard-layout-preview"
          isScrollable
          isStretched
          hasPadding
        >
          <Stack>
            {explanations &&
              explanations.map((explanationProps) => {
                if (explanationProps) {
                  return (
                    <Explanation
                      key={explanationProps.explanationKey}
                      explanationKey={explanationProps.explanationKey}
                      shortExplanationKey={explanationProps.shortExplanationKey}
                      icon={explanationProps.icon}
                    />
                  );
                }
                return null;
              })}
            {descriptionsKeys &&
              descriptionsKeys.map((descriptionKey) => (
                <Cluster space={2} wrap="nowrap">
                  <SettingOutlined
                    style={{
                      fontSize: '2.5rem',
                    }}
                  />
                  <Markdown source={t(descriptionKey)} />
                </Cluster>
              ))}
          </Stack>
          {preview && (
            <Card className="project-wizard-layout-preview">{preview}</Card>
          )}
          <Cluster justify="end">
            <Button
              data-testid="button-Back"
              icon={<ChevronLeftIcon />}
              onClick={onOpenPrevTab}
              disabled={!hasPrevTab}
            >
              Back
            </Button>
            {hasNextTab ? (
              <Button
                data-testid="button-Next"
                type="primary"
                onClick={onOpenNextTab}
              >
                <div className="cluster-2 items-center">
                  <span>Next</span>
                  <ChevronRightIcon className="h-5 w-5" />
                </div>
              </Button>
            ) : (
              <Button
                data-testid="button-Complete"
                type="primary"
                icon={<CheckOutlined />}
                onClick={onComplete}
              >
                {t('complete')}
              </Button>
            )}
          </Cluster>
        </Cover>
      )}
    </Container>
  );
};

Layout.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  forms: PropTypes.array,
  // eslint-disable-next-line react/forbid-prop-types
  descriptionsKeys: PropTypes.array,
};

Layout.defaultProps = {
  forms: [],
  descriptionsKeys: [],
};

export default Layout;
